import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { DimensionCount } from "hooks/useCrossFilter"

// A stacked segment of each bar (e.g. Co-ops / Employees). Its value is read from data[dataKey].
export interface BarStack {
    dataKey: string
    label: string
    className: string
}

type BarRow = DimensionCount & Record<string, any>

interface BarChartCardProps {
    title: string
    data: BarRow[]
    selected: string[]
    // additive = Ctrl/Cmd was held (add to the selection instead of replacing it)
    onSelect: (value: string, additive: boolean) => void
    // Clears this chart's selection (shown next to "N selected")
    onClear?: () => void
    // Default: largest count first. "label" keeps the order given (e.g. years of service).
    order?: "count" | "label"
    // What one unit is called in the tooltip ("employees", "terminations"...)
    unit?: string
    // Split each bar into segments; `count` stays the bar total
    stacks?: BarStack[]
    // Take the full width of the chart grid
    wide?: boolean
}

const ROW_HEIGHT = 28
const MAX_LABEL = 24
const truncate = (text: string) => (text.length > MAX_LABEL ? `${text.slice(0, MAX_LABEL - 1)}…` : text)

const ChartTooltip = ({ active, payload, unit, stacks }: any) => {
    if (!active || !payload?.length) return null
    const row = payload[0].payload as BarRow
    return (
        <div className="hr-report-tooltip">
            <strong>{row.value}</strong>
            <span>{row.count} {unit}</span>
            {(stacks as BarStack[] | undefined)?.map(s => <span key={s.dataKey}>{s.label}: {row[s.dataKey] ?? 0}</span>)}
        </div>
    )
}

// One horizontal bar per value. Clicking a bar filters the whole page by it (Ctrl/Cmd + click adds
// it to the selection); values not picked are dimmed. The chart grows with its rows: no inner scroll.
const BarChartCard = ({ title, data, selected, onSelect, onClear, order = "count", unit = "employees", stacks, wide = false }: BarChartCardProps) => {
    const rows = order === "count"
        ? [...data].sort((a, b) => b.count - a.count || a.value.localeCompare(b.value))
        : data
    const hasSelection = selected.length > 0
    const barClass = (r: BarRow, className = "") => `hr-report-bar ${className} ${hasSelection && !selected.includes(r.value) ? "is-dimmed" : ""}`
    const segments = stacks ?? [{ dataKey: "count", label: "", className: "" }]

    return (
        <div className={`section-container hr-report-card ${wide ? "is-wide" : ""}`}>
            <header className="hr-report-card-header">
                <h5>{title}</h5>
                <div className="d-flex align-items-center gap-3">
                    {stacks && <div className="hr-report-legend">
                        {stacks.map(s => <span key={s.dataKey}><i className={`hr-report-legend-swatch ${s.className}`} />{s.label}</span>)}
                    </div>}
                    {hasSelection && <span className="hr-report-card-hint">
                        {selected.length} selected
                        {onClear && <button type="button" className="btn btn-link btn-sm p-0 ms-2 align-baseline" onClick={onClear}>Clear</button>}
                    </span>}
                </div>
            </header>
            {rows.length === 0
                ? <p className="text-muted mb-0">No data for the current filters.</p>
                : (
                    <div style={{ height: rows.length * ROW_HEIGHT + 32 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={rows}
                                layout="vertical"
                                margin={{ top: 4, right: 36, left: 4, bottom: 4 }}
                                barCategoryGap={6}
                                style={{ cursor: "pointer" }}
                                onClick={(state: any, e: any) => {
                                    if (state?.activeLabel !== undefined)
                                        onSelect(String(state.activeLabel), !!(e?.ctrlKey || e?.metaKey))
                                }}
                            >
                                <CartesianGrid horizontal={false} className="hr-report-gridlines" />
                                <XAxis type="number" allowDecimals={false} hide />
                                <YAxis
                                    type="category"
                                    dataKey="value"
                                    width={170}
                                    tickLine={false}
                                    axisLine={false}
                                    interval={0}
                                    tickFormatter={truncate}
                                    className="hr-report-axis"
                                />
                                <Tooltip
                                    content={<ChartTooltip unit={unit} stacks={stacks} />}
                                    cursor={{ className: "hr-report-cursor" }}
                                    isAnimationActive={false}
                                />
                                {segments.map((s, i) => {
                                    const isLast = i === segments.length - 1
                                    return <Bar
                                        key={s.dataKey}
                                        dataKey={s.dataKey}
                                        stackId={stacks ? "stack" : undefined}
                                        radius={isLast ? [0, 4, 4, 0] : undefined}
                                        isAnimationActive={false}
                                    >
                                        {rows.map(r => <Cell key={r.value} className={barClass(r, s.className)} />)}
                                        {/* The total sits at the end of the bar */}
                                        {isLast && <LabelList dataKey="count" position="right" className="hr-report-bar-label" />}
                                    </Bar>
                                })}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
        </div>
    )
}

export default BarChartCard
